import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, timeout } from 'rxjs';
import { CollectionRequest, CollectionsResponse, HealthStatus, IndexRequest, IndexResponse, MdQueryConnection, MessageResponse, NoteContentResult, SearchMode, SearchRequest, SearchResponse } from './md-query.models';



export function mdQueryApiBaseUrl(url: string): string {
  const baseUrl = url.trim().replace(/\/+$/, '');
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
}

export function mdQueryError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) return '無法連線至 md-query，請確認 API 位址、服務與 CORS 設定。';
    if (error.status === 401) return 'API 驗證失敗，請確認 Bearer Token。';
    const body: unknown = error.error;
    if (body && typeof body === 'object') {
      if ('error' in body && typeof body.error === 'string') return body.error;
      if ('message' in body && typeof body.message === 'string') return body.message;
    }
    return `API 請求失敗（HTTP ${error.status}）`;
  }
  return error instanceof Error ? error.message : '操作失敗，請稍後重試。';
}

@Injectable({ providedIn: 'root' })
export class MdQueryApiService {
  private readonly http = inject(HttpClient);

  private url(connection: MdQueryConnection, path: string): string {
    return `${mdQueryApiBaseUrl(connection.baseUrl)}/${path}`;
  }
  private headers(connection: MdQueryConnection): HttpHeaders {
    return connection.token?.trim() ? new HttpHeaders({ Authorization: `Bearer ${connection.token.trim()}` }) : new HttpHeaders();
  }
  getHealth(connection: MdQueryConnection): Observable<HealthStatus> {
    return this.http.get<HealthStatus>(this.url(connection, 'health'), { headers: this.headers(connection) }).pipe(timeout(15000));
  }
  getCollections(connection: MdQueryConnection): Observable<CollectionsResponse> {
    return this.http.get<CollectionsResponse>(this.url(connection, 'collections'), { headers: this.headers(connection) }).pipe(timeout(15000));
  }
  addCollection(connection: MdQueryConnection, data: CollectionRequest): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(this.url(connection, 'collections'), data, { headers: this.headers(connection) });
  }
  deleteCollection(connection: MdQueryConnection, name: string): Observable<MessageResponse> {
    return this.http.delete<MessageResponse>(this.url(connection, `collections/${encodeURIComponent(name)}`), { headers: this.headers(connection) });
  }
  triggerReindex(connection: MdQueryConnection, data: IndexRequest = {}): Observable<IndexResponse> {
    return this.http.post<IndexResponse>(this.url(connection, 'index'), data, { headers: this.headers(connection) });
  }
  search(connection: MdQueryConnection, mode: SearchMode, payload: SearchRequest): Observable<SearchResponse> {
    return this.http.post<SearchResponse>(this.url(connection, mode), payload, { headers: this.headers(connection) });
  }
  getNote(connection: MdQueryConnection, collection: string, path: string, startLine?: number, endLine?: number): Observable<NoteContentResult> {
    let params = new HttpParams().set('collection', collection).set('path', path);
    if (startLine !== undefined) params = params.set('startLine', startLine);
    if (endLine !== undefined) params = params.set('endLine', endLine);
    return this.http.get<NoteContentResult>(this.url(connection, 'notes'), { params, headers: this.headers(connection) }).pipe(timeout(15000));
  }
}
